import React, {Component} from 'react';
import {withTranslation} from 'react-i18next';
import {Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {API_URL} from "../../../settings/local_settings";
import {APIClient} from "../../../utils/APIClient";
import {FaIcon} from "../../FaIcon";
import {ChipFormulaInput} from "./ChipFormulaInput";
import {FormulaToolboxSidebar} from "./FormulaToolboxSidebar";
import {stripChannelPrefix} from "./metric_search";
import "../CustomMetrics.scss";


const VALUE_FORMAT_OPTIONS = [
    {value: 'INTEGER', label: 'Entero'},
    {value: 'DECIMAL', label: 'Decimal'},
    {value: 'PERCENTAGE', label: 'Porcentaje'},
    {value: 'CURRENCY', label: 'Moneda'},
];

const MAX_FORMULA_LENGTH = 500;


// "Hace X días/horas/minutos" relativo a now. Sin libs externas — el bundle ya pesa lo suficiente.
function formatRelativeEs(isoDate) {
    if (!isoDate) return '';
    const then = new Date(isoDate).getTime();
    if (isNaN(then)) return '';
    const diffMs = Date.now() - then;
    if (diffMs < 0) return 'ahora mismo';
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return 'hace instantes';
    if (min < 60) return `hace ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.floor(h / 24);
    if (d < 30) return `hace ${d} ${d === 1 ? 'día' : 'días'}`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `hace ${mo} ${mo === 1 ? 'mes' : 'meses'}`;
    const y = Math.floor(mo / 12);
    return `hace ${y} ${y === 1 ? 'año' : 'años'}`;
}


function slugifyName(s) {
    if (!s) return '';
    return s
        .toLowerCase()
        .replace(/[áàäâã]/g, 'a').replace(/[éèëê]/g, 'e').replace(/[íìïî]/g, 'i')
        .replace(/[óòöô]/g, 'o').replace(/[úùüû]/g, 'u').replace(/[ñ]/g, 'n')
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .substring(0, 63)
        .replace(/^[0-9]+/, '');  // name must start with letter
}


class CustomMetricEditor extends Component {

    constructor(props) {
        super(props);
        const m = props.metric;
        const isEdit = !!m;
        this.state = {
            isEdit,
            displayName: m ? m.display_name : '',
            name: m ? m.name : '',
            nameAutoDerived: !isEdit,  // auto-slugify hasta que el user edite name manualmente
            // En CREATE el identifier se auto-deriva y queda oculto detrás de un disclosure
            // "avanzado". El 99% de los users no lo necesita (todo el UI usa display_name);
            // solo power-users que referencian la métrica desde el MCP por su slug.
            showIdentifierAdvanced: false,
            formula: m ? m.formula : '',
            valueFormat: m ? m.value_format : 'DECIMAL',
            higherIsBetter: m ? m.higher_is_better : true,
            updatedAt: m ? m.updated_at : null,
            goalsCount: m ? (m.goals_count || 0) : 0,
            // Catálogo para el toolbox sidebar. Lazy-load en componentDidMount.
            metricCatalog: [],
            customMetricsList: [],
            catalogById: {},          // {identifier: {displayName, ...}}, derivado de los dos arriba
            catalogLoaded: false,
            // preview/validation state
            previewLoading: false,
            previewResult: null,        // {requires_post_aggregation, referenced_metric_names, referenced_custom_metric_ids}
            previewError: null,         // {error_code, error_message}
            saving: false,
            saveError: null,
        };
        this._dryRunTimer = null;
        this._chipInputRef = React.createRef();
        this.handleDisplayNameChange = this.handleDisplayNameChange.bind(this);
        this.handleNameChange = this.handleNameChange.bind(this);
        this.handleFormulaChange = this.handleFormulaChange.bind(this);
        this.handleValueFormatChange = this.handleValueFormatChange.bind(this);
        this.handleHigherIsBetterChange = this.handleHigherIsBetterChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleInsertToken = this.handleInsertToken.bind(this);
    }

    componentDidMount() {
        if (this.state.formula) this._scheduleDryRun();
        this._fetchCatalogs();
    }

    _fetchCatalogs() {
        // Catálogo built-in (Data Node fields TYPE_METRIC).
        new APIClient(API_URL + 'agency/data-node-metric-fields/').call('GET').then((response) => {
            const fields = (response && response.fields) || [];
            this.setState((prev) => ({
                metricCatalog: fields,
                catalogById: this._buildCatalogById(fields, prev.customMetricsList),
                catalogLoaded: true,
            }));
        });
        // Catálogo de custom metrics propias de la agency (excluyendo la que estamos editando).
        new APIClient(API_URL + 'agency/custom-metrics/').call('GET').then((response) => {
            const customs = (response && response.custom_metrics) || [];
            const editingId = this.props.metric && this.props.metric.id;
            const filtered = editingId ? customs.filter((cm) => cm.id !== editingId) : customs;
            this.setState((prev) => ({
                customMetricsList: filtered,
                catalogById: this._buildCatalogById(prev.metricCatalog, filtered),
            }));
        });
    }

    _buildCatalogById(metricCatalog, customMetricsList) {
        // Map identifier → {displayName, ...} consumido por ChipFormulaInput para
        // renderizar chips desde una fórmula string (modo edición).
        // El displayName que entregamos al chip es el "clean" — sin el prefijo del canal
        // ("Meta Ads - Conversaciones" → "Conversaciones"). Evita chips gigantes que
        // revientan la fórmula a multilínea, y consistente con cómo el toolbox los lista.
        const map = {};
        (metricCatalog || []).forEach((m) => {
            const raw = m.display_name || m.id;
            const group = m.group || 'Otros';
            map[m.id] = {displayName: stripChannelPrefix(raw, group), isCustom: false};
        });
        (customMetricsList || []).forEach((cm) => {
            map[cm.name] = {displayName: cm.display_name || cm.name, isCustom: true};
        });
        return map;
    }

    componentWillUnmount() {
        if (this._dryRunTimer) clearTimeout(this._dryRunTimer);
    }

    handleDisplayNameChange(e) {
        const displayName = e.target.value;
        this.setState((prev) => ({
            displayName,
            // Auto-deriva el identifier solo en CREATE y mientras user no haya editado name manualmente.
            name: (!prev.isEdit && prev.nameAutoDerived) ? slugifyName(displayName) : prev.name,
        }));
    }

    handleNameChange(e) {
        // En edit, el name es inmutable (backend rechaza con NAME_IMMUTABLE).
        if (this.state.isEdit) return;
        this.setState({name: e.target.value, nameAutoDerived: false});
    }

    handleFormulaChange(formula) {
        // Recibe el string serializado de ChipFormulaInput (no es un event nativo).
        this.setState({formula, saveError: null}, () => this._scheduleDryRun());
    }

    handleInsertToken(token) {
        // Sidebar hace click en una métrica / operador. Insertamos en el cursor
        // del ChipFormulaInput, que dispara onChange y re-corre el dry-run.
        if (this._chipInputRef.current) {
            this._chipInputRef.current.insertToken(token);
        }
    }

    handleValueFormatChange(e) {
        this.setState({valueFormat: e.target.value});
    }

    handleHigherIsBetterChange(e) {
        this.setState({higherIsBetter: e.target.checked});
    }

    _scheduleDryRun() {
        if (this._dryRunTimer) clearTimeout(this._dryRunTimer);
        this._dryRunTimer = setTimeout(() => this._runDryRun(), 500);
    }

    _runDryRun() {
        const {formula, name, isEdit} = this.state;
        if (!formula || !formula.trim()) {
            this.setState({previewResult: null, previewError: null, previewLoading: false});
            return;
        }
        if (formula.length > MAX_FORMULA_LENGTH) {
            this.setState({
                previewError: {error_code: 'FORMULA_TOO_LONG', error_message: `La fórmula excede ${MAX_FORMULA_LENGTH} caracteres.`},
                previewResult: null, previewLoading: false,
            });
            return;
        }

        this.setState({previewLoading: true});
        const payload = {formula};
        if (name) payload.name = name;
        if (isEdit && this.props.metric) payload.editing_id = this.props.metric.id;

        new APIClient(API_URL + 'agency/custom-metrics/dry-run/').call('POST', payload)
            .then((response) => {
                if (response && response.error_code) {
                    this.setState({
                        previewError: response,
                        previewResult: null,
                        previewLoading: false,
                    });
                } else {
                    this.setState({
                        previewResult: response,
                        previewError: null,
                        previewLoading: false,
                    });
                }
            })
            .catch(() => {
                this.setState({previewError: null, previewLoading: false});
            });
    }

    _payloadForSubmit() {
        const {displayName, name, formula, valueFormat, higherIsBetter, isEdit} = this.state;
        if (isEdit) {
            return {
                display_name: displayName,
                formula,
                value_format: valueFormat,
                higher_is_better: higherIsBetter,
            };
        }
        return {
            name,
            display_name: displayName,
            formula,
            value_format: valueFormat,
            higher_is_better: higherIsBetter,
        };
    }

    handleSubmit() {
        const {isEdit} = this.state;
        const payload = this._payloadForSubmit();

        if (!payload.display_name || !payload.formula) {
            this.setState({saveError: {error_message: 'Nombre y fórmula son obligatorios.'}});
            return;
        }
        if (!isEdit && !payload.name) {
            this.setState({saveError: {error_message: 'El identificador es obligatorio.'}});
            return;
        }

        this.setState({saving: true, saveError: null});

        const url = isEdit
            ? `${API_URL}agency/custom-metrics/${this.props.metric.id}/`
            : `${API_URL}agency/custom-metrics/`;
        const method = isEdit ? 'PATCH' : 'POST';

        // Optimistic locking (addenda A6) en PATCH.
        const extraHeaders = {};
        if (isEdit && this.state.updatedAt) {
            try {
                extraHeaders['If-Unmodified-Since'] = new Date(this.state.updatedAt).toUTCString();
            } catch (_) {}
        }

        new APIClient(url).call(method, payload, {extraHeaders})
            .then((response) => {
                if (!response) {
                    this.setState({saving: false, saveError: {error_message: 'Error de red. Reintentá.'}});
                    return;
                }
                if (response.error_code || response.status === 'ERROR') {
                    this.setState({saving: false, saveError: response});
                    return;
                }
                this.setState({saving: false});
                this.props.onSaved && this.props.onSaved(
                    response.custom_metric, isEdit ? 'update' : 'create');
            })
            .catch((err) => {
                this.setState({saving: false, saveError: {error_message: 'Error de red. Reintentá.'}});
            });
    }

    render() {
        const {t, isOpen, onClose} = this.props;
        const {isEdit, displayName, name, formula, valueFormat, higherIsBetter, updatedAt,
               previewLoading, previewResult, previewError, saving, saveError, goalsCount,
               metricCatalog, customMetricsList, catalogById, catalogLoaded} = this.state;

        const formulaLengthLabel = `${formula.length} / ${MAX_FORMULA_LENGTH}`;
        const formulaTooLong = formula.length > MAX_FORMULA_LENGTH;
        const lastEditLabel = isEdit && updatedAt ? formatRelativeEs(updatedAt) : '';

        return (
            <Modal isOpen={isOpen} toggle={onClose} size="xl" className="custom-metric-editor-modal">
                <ModalHeader toggle={onClose}>
                    {isEdit
                        ? t('custom_metrics.editor.edit_title', 'Editar métrica personalizada')
                        : t('custom_metrics.editor.create_title', 'Nueva métrica personalizada')}
                </ModalHeader>
                <ModalBody>
                    <div className="modal-section">

                        {isEdit && goalsCount > 0 && (
                            <div className="custom-metric-banner -warning">
                                <span className="custom-metric-banner-icon">
                                    <FaIcon iconName="circle-exclamation"/>
                                </span>
                                <div>
                                    <strong>
                                    {t('custom_metrics.editor.warning_with_goals',
                                        'Esta métrica tiene {{count}} goals activos.', {count: goalsCount})}
                                    </strong>{' '}
                                    {t('custom_metrics.editor.warning_with_goals_detail',
                                        'Modificar la fórmula recalcula valores históricos en el dashboard y puede disparar alertas en el próximo sync.')}
                                </div>
                            </div>
                        )}

                        <div className="form-row">
                            <label>{t('custom_metrics.editor.display_name', 'Nombre visible')}</label>
                            <input type="text" className="form-control" value={displayName}
                                   onChange={this.handleDisplayNameChange}
                                   placeholder={t('custom_metrics.editor.display_name_placeholder', 'Ej. Ventas estimativas')}/>
                        </div>

                        {isEdit ? (
                            <div className="custom-metric-identifier-inline">
                                <span className="custom-metric-identifier-label">
                                    {t('custom_metrics.editor.name_label_short', 'Identificador interno')}
                                </span>
                                <code className="custom-metric-identifier-value">{name}</code>
                                <span className="custom-metric-identifier-help">
                                    — {t('custom_metrics.editor.name_immutable_short_v2',
                                        'se usa para referenciar la métrica desde el MCP. No se puede cambiar.')}
                                </span>
                            </div>
                        ) : !this.state.showIdentifierAdvanced ? (
                            <div className="custom-metric-identifier-disclosure">
                                <button type="button"
                                        className="custom-metric-identifier-toggle"
                                        onClick={() => this.setState({showIdentifierAdvanced: true})}>
                                    {t('custom_metrics.editor.name_advanced',
                                        'Personalizar identificador (avanzado)')}
                                </button>
                            </div>
                        ) : (
                            <div className="form-row">
                                <label>{t('custom_metrics.editor.name', 'Identificador (uso interno)')}</label>
                                <input type="text" className="form-control" value={name}
                                       onChange={this.handleNameChange}
                                       placeholder="ej. ventas_estimativas"/>
                                <small className="custom-metric-name-help">
                                    {t('custom_metrics.editor.name_advanced_help',
                                        'Slug técnico que va a tener esta métrica. Se auto-deriva del nombre visible. Solo modificalo si vas a referenciarla desde el MCP.')}
                                </small>
                            </div>
                        )}

                        <div className="form-row">
                            <label>{t('custom_metrics.editor.value_format', 'Formato del resultado')}</label>
                            <select className="form-control custom-metric-value-format-select"
                                    value={valueFormat}
                                    onChange={this.handleValueFormatChange}>
                                {VALUE_FORMAT_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <small className="form-text text-muted">
                                {t('custom_metrics.editor.value_format_help',
                                    'La fórmula devolverá un valor en este formato.')}
                            </small>
                        </div>

                        <div className="form-row">
                            <div className="custom-metric-formula-label">
                                <label>{t('custom_metrics.editor.formula', 'Fórmula')}</label>
                                <span className="custom-metric-formula-hint">
                                    {t('custom_metrics.editor.formula_help_inline',
                                        'Operadores: + − × ÷ y paréntesis · Usá métricas existentes o constantes numéricas')}
                                </span>
                            </div>
                            <div className="custom-metric-formula-2col">
                                <div className="custom-metric-formula-input-col">
                                    {/* Race-fix: gateamos el mount de ChipFormulaInput hasta tener
                                        el catálogo. Sin esto, los chips de la fórmula existente se
                                        marcan como `-broken` porque `catalogById` arranca vacío en
                                        el primer mount y `_renderFormula` no se re-ejecuta cuando
                                        el catálogo arriba (DOM es source-of-truth post-mount). */}
                                    {catalogLoaded ? (
                                        <ChipFormulaInput
                                            ref={this._chipInputRef}
                                            initialValue={formula}
                                            catalogById={catalogById}
                                            onChange={this.handleFormulaChange}
                                            placeholder={t('custom_metrics.editor.formula_placeholder', 'Hacé click en una métrica del panel derecho o tipeá una constante numérica…')}/>
                                    ) : (
                                        <div className="chip-formula-input -loading">
                                            <em style={{color: '#999'}}>{t('common.loading', 'Cargando…')}</em>
                                        </div>
                                    )}

                                    {/* Validation banner unificado: success/error/empty + len counter a la derecha.
                                        El "post-aggregation" detail se colapsa a un ícono con tooltip nativo —
                                        evita romper la fila a 4-5 líneas cuando la columna queda angosta. */}
                                    <div className={`custom-metric-validation${previewError ? ' -error' : ''}${!previewError && previewResult ? ' -success' : ''}`}>
                                        <span className="custom-metric-validation-icon">
                                            {previewLoading && <FaIcon iconName="spinner"/>}
                                            {!previewLoading && previewError && <FaIcon iconName="circle-xmark"/>}
                                            {!previewLoading && !previewError && previewResult && <FaIcon iconName="circle-check"/>}
                                            {!previewLoading && !previewError && !previewResult && <FaIcon iconName="circle-info"/>}
                                        </span>
                                        <span className="custom-metric-validation-text">
                                            {previewLoading && t('common.validating', 'Validando…')}
                                            {!previewLoading && previewError && (
                                                <><strong>{previewError.error_code}:</strong> {previewError.error_message}</>
                                            )}
                                            {!previewLoading && !previewError && previewResult && (
                                                <>
                                                    <strong>{t('custom_metrics.editor.valid', 'Fórmula válida.')}</strong>
                                                    {previewResult.requires_post_aggregation && (
                                                        <>
                                                            {' '}
                                                            <span className="custom-metric-post-agg-tip"
                                                                  title={t('custom_metrics.editor.requires_post_agg',
                                                                      'Contiene componente live-api: la métrica se calcula post-agregación. En goals tipo "today" puede mostrar — mientras esperamos el sync diario.')}>
                                                                <FaIcon iconName="circle-info"/> {t('custom_metrics.editor.live_api_short', 'Live-API')}
                                                            </span>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                            {!previewLoading && !previewError && !previewResult && !formula && (
                                                t('custom_metrics.editor.preview_empty', 'Escribí una fórmula para validar.')
                                            )}
                                        </span>
                                        <span className={`custom-metric-formula-length${formulaTooLong ? ' -too-long' : ''}`}>{formulaLengthLabel}</span>
                                    </div>

                                    {/* Dirección preferida: requiere decisión del user — debe quedar arriba
                                        del currency warning (info pasiva). */}
                                    <div className="custom-metric-direction-block">
                                        <label className="custom-metric-direction-label">
                                            {t('custom_metrics.editor.direction_label', 'Dirección preferida')}
                                        </label>
                                        <div className="cm-direction-segmented" role="radiogroup"
                                             aria-label={t('custom_metrics.editor.direction_label', 'Dirección preferida')}>
                                            <button type="button"
                                                    role="radio"
                                                    aria-checked={higherIsBetter}
                                                    className={`cm-direction-segmented__option ${higherIsBetter ? 'is-active' : ''}`}
                                                    onClick={() => this.handleHigherIsBetterChange({target: {checked: true}})}>
                                                <FaIcon iconName="arrow-up"/>
                                                {t('custom_metrics.editor.direction_higher', 'Más alto es mejor')}
                                            </button>
                                            <button type="button"
                                                    role="radio"
                                                    aria-checked={!higherIsBetter}
                                                    className={`cm-direction-segmented__option ${!higherIsBetter ? 'is-active' : ''}`}
                                                    onClick={() => this.handleHigherIsBetterChange({target: {checked: false}})}>
                                                <FaIcon iconName="arrow-down"/>
                                                {t('custom_metrics.editor.direction_lower', 'Más bajo es mejor')}
                                            </button>
                                        </div>
                                        <small className="form-text text-muted">
                                            {t('custom_metrics.editor.direction_help',
                                                'ROAS ▲ · CPA ▼ — afecta alertas y flechas de tendencia.')}
                                        </small>
                                    </div>

                                    {/* Currency warning como banner amarillo */}
                                    <div className="custom-metric-currency-warning">
                                        <span className="custom-metric-currency-warning-icon">
                                            <FaIcon iconName="triangle-exclamation"/>
                                        </span>
                                        <span>
                                            <strong>{t('custom_metrics.editor.currency_disclaimer_strong',
                                                'No mezcles métricas en distintas monedas')}</strong>
                                            {' — '}
                                            {t('custom_metrics.editor.currency_disclaimer_detail',
                                                'el sistema no convierte ni avisa al evaluar.')}
                                        </span>
                                    </div>
                                </div>
                                <div className="custom-metric-formula-sidebar-col">
                                    <FormulaToolboxSidebar
                                        metricCatalog={metricCatalog}
                                        customMetrics={customMetricsList}
                                        formula={formula}
                                        onInsertToken={this.handleInsertToken}/>
                                </div>
                            </div>
                        </div>

                        {saveError && (
                            <div className="custom-metric-save-error">
                                <strong>{saveError.error_code || 'Error'}:</strong>{' '}
                                {saveError.error_message || 'No se pudo guardar.'}
                            </div>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    {lastEditLabel && (
                        <span className="custom-metric-footer-meta">
                            {t('custom_metrics.editor.last_edit', 'Última edición')}: <b>{lastEditLabel}</b>
                        </span>
                    )}
                    <button className="btn -alt -negative" onClick={onClose} disabled={saving}>
                        {t('common.cancel', 'Cancelar')}
                    </button>
                    <button className="btn -alt"
                            onClick={this.handleSubmit}
                            disabled={saving || formulaTooLong || !!previewError || !formula || !displayName || (!isEdit && !name)}>
                        {saving
                            ? t('common.saving', 'Guardando...')
                            : (isEdit
                                ? t('common.save', 'Guardar')
                                : t('custom_metrics.editor.create_button', 'Crear métrica'))}
                    </button>
                </ModalFooter>
            </Modal>
        );
    }
}

export default withTranslation()(CustomMetricEditor);
